import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { every, isEmpty } from 'underscore';
import SteppedModal from 'components/SteppedModal';
import useConnectedModal from 'containers/ConnectedModal/useConnectedModal';
import LinkedInPostModal from 'containers/LinkedInPostModal';
import { LinkedinPostModalSteps } from 'containers/LinkedInPostModal/steps/types';
import useAccessDeniedStep from 'containers/LinkedInPostModal/steps/useAccessDeniedStep';
import useAuthenticationProgressStep from 'containers/LinkedInPostModal/steps/useAuthenticationProgressStep';
import useAuthorizationStep from 'containers/LinkedInPostModal/steps/useAuthorizationStep';
import useAutopostingComposerStep from 'containers/LinkedInPostModal/steps/useAutopostingComposerStep';
import { UseComposerStepValue } from 'containers/LinkedInPostModal/steps/useAutopostingComposerStep/types';
import useSwitchAccountStep from 'containers/LinkedInPostModal/steps/useSwitchAccountStep';
import { showError } from 'redux/modules/notification';
import { linkedinUserDataSelector } from 'redux/modules/social';
import { LinkedInSheetDefaultValues } from './types';

export interface LinkedInAutoPostModalProps {}

const ACCESS_DENIED_MESSAGE = 'access_denied';
const ACCESS_DENIED_STEP = 'access-denied';
const AUTHORIZATION_STEP = 'authorization';

const LinkedInAutoPostModal: React.FC<LinkedInAutoPostModalProps> = () => {
  const [activeStep, setActiveStep] = useState('');

  const modalRef = useRef<SteppedModal>(null);

  const linkedInUserData = useSelector(linkedinUserDataSelector);

  const { params, show, onExited, onHide } = useConnectedModal(
    'LinkedInAutoPost',
  );

  const dispatch = useDispatch();

  const { subscriptionItem } = params ?? {};

  const { description, linkedinAuthorId } =
    subscriptionItem?.autoPostVideoPreference.options || {};

  const initialValues = React.useMemo((): LinkedInSheetDefaultValues => {
    return {
      description:
        description ??
        'Here’s a new clip from <Episode Title>! Listen to the full episode here: <Link To Episode>',
      linkedinAuthorId,
    };
  }, [description, linkedinAuthorId]);

  const replaceModalStep = useCallback((id: LinkedinPostModalSteps): void => {
    modalRef.current?.stepHistory.replace(id);
  }, []);

  const handleGoToProgressStep = useCallback(() => {
    replaceModalStep('authentication-in-progress');
  }, [replaceModalStep]);

  const handleSwitchAccount = useCallback(() => {
    replaceModalStep('switch');
  }, [replaceModalStep]);

  const handleGoToComposerStep = useCallback(() => {
    if (every(linkedInUserData, isEmpty)) {
      return undefined;
    }

    return replaceModalStep('composer');
  }, [linkedInUserData, replaceModalStep]);

  const handleAuthenticationSuccess = useCallback(async () => {
    replaceModalStep('composer');
  }, [replaceModalStep]);

  const handleHide = useCallback(
    (values?: UseComposerStepValue) => {
      handleGoToComposerStep();
      onHide(values);
    },
    [handleGoToComposerStep, onHide],
  );

  const handleAuthorizationError = useCallback(
    (err: Error) => {
      if (err.message === ACCESS_DENIED_MESSAGE) {
        modalRef?.current?.stepHistory.replace(ACCESS_DENIED_STEP);
      } else {
        modalRef?.current?.stepHistory.replace(AUTHORIZATION_STEP);
        onHide();
        dispatch(showError('Error authenticating with LinkedIn'));
      }
    },
    [dispatch, onHide],
  );

  const handleExited = useCallback(() => {
    handleGoToComposerStep();
    onExited();
  }, [handleGoToComposerStep, onExited]);

  const handleSubmit = useCallback(
    (values: UseComposerStepValue): void => {
      handleHide(values);
    },
    [handleHide],
  );

  const authorization = useAuthorizationStep({
    onAuth: handleGoToProgressStep,
    onAuthSuccess: handleAuthenticationSuccess,
    onError: handleAuthorizationError,
  });

  const authenticationProgress = useAuthenticationProgressStep();

  const accessDenied = useAccessDeniedStep({
    onError: () => null,
    onReviseAccess: () => null,
  });

  const composer = useAutopostingComposerStep({
    initialValues,
    onHideModal: handleHide,
    onSubmit: handleSubmit,
    onSwitchAccount: handleSwitchAccount,
  });

  const isComposerStep = activeStep === 'composer';

  const switchAccount = useSwitchAccountStep({
    onSwitchAccount: handleGoToProgressStep,
    onSwitchAccountSuccess: handleAuthenticationSuccess,
    onError: handleAuthorizationError,
  });

  return (
    <LinkedInPostModal
      {...{ show }}
      ref={modalRef}
      title={!isComposerStep && 'auto-post videos'}
      defaultStep={authorization.id}
      steps={[
        authorization,
        authenticationProgress,
        accessDenied,
        composer,
        switchAccount,
      ]}
      className={isComposerStep && 'composer-step-container'}
      onStepChange={setActiveStep}
      onHide={() => handleHide()}
      onExited={handleExited}
    />
  );
};

export default LinkedInAutoPostModal;
